import {
  SHARED_PHYSICS_MAGNET_MAX_PX,
  SHARED_PHYSICS_MAGNET_PULL,
  SHARED_PHYSICS_MAGNET_RADIUS_PX,
  SHARED_PHYSICS_MAGNET_SELECTORS,
  SHARED_PHYSICS_RIPPLE_DURATION_MS,
  SHARED_PHYSICS_RIPPLE_SELECTORS,
  SHARED_PHYSICS_TILT_MAX_DEG,
  SHARED_PHYSICS_TILT_SELECTORS,
} from '#shared/constants/physics'

const RIPPLE_SELECTOR = SHARED_PHYSICS_RIPPLE_SELECTORS.join(',')
const TILT_SELECTOR = SHARED_PHYSICS_TILT_SELECTORS.join(',')
const MAGNET_SELECTOR = SHARED_PHYSICS_MAGNET_SELECTORS.join(',')

const clampMagnet = (value: number) => Math.max(-SHARED_PHYSICS_MAGNET_MAX_PX, Math.min(SHARED_PHYSICS_MAGNET_MAX_PX, value))

const closestMatch = (node: EventTarget | null, selector: string) => (node instanceof Element ? node.closest<HTMLElement>(selector) : null)

export default defineNuxtPlugin({
  name: 'app:interaction-physics',

  setup() {
    const root = document.documentElement
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)')

    const layer = document.createElement('div')
    layer.className = 'app-physics'
    layer.setAttribute('aria-hidden', 'true')
    document.body.append(layer)

    const pointer = { x: 0, y: 0 }
    let tiltTarget: HTMLElement | null = null
    let magnetTarget: HTMLElement | null = null
    let frameRequest = 0

    const isDisabled = () => reducedMotion.matches || root.classList.contains('is-tv')

    const fitFrame = (frame: HTMLElement, target: HTMLElement) => {
      const rect = target.getBoundingClientRect()
      frame.style.left = `${rect.left}px`
      frame.style.top = `${rect.top}px`
      frame.style.width = `${rect.width}px`
      frame.style.height = `${rect.height}px`
      frame.style.borderRadius = getComputedStyle(target).borderRadius
      return rect
    }

    const clearTilt = () => {
      tiltTarget?.style.removeProperty('--app-tilt-x')
      tiltTarget?.style.removeProperty('--app-tilt-y')
      tiltTarget = null
    }

    const clearMagnet = () => {
      magnetTarget?.style.removeProperty('--app-magnet-x')
      magnetTarget?.style.removeProperty('--app-magnet-y')
      magnetTarget = null
    }

    const isNearMagnet = (target: HTMLElement) => {
      const rect = target.getBoundingClientRect()
      return pointer.x >= rect.left - SHARED_PHYSICS_MAGNET_RADIUS_PX
        && pointer.x <= rect.right + SHARED_PHYSICS_MAGNET_RADIUS_PX
        && pointer.y >= rect.top - SHARED_PHYSICS_MAGNET_RADIUS_PX
        && pointer.y <= rect.bottom + SHARED_PHYSICS_MAGNET_RADIUS_PX
    }

    const releaseAll = () => {
      clearTilt()
      clearMagnet()
    }

    const render = () => {
      frameRequest = 0

      if (magnetTarget?.isConnected) {
        const rect = magnetTarget.getBoundingClientRect()
        const offsetX = pointer.x - (rect.left + rect.width / 2)
        const offsetY = pointer.y - (rect.top + rect.height / 2)
        magnetTarget.style.setProperty('--app-magnet-x', `${clampMagnet(offsetX * SHARED_PHYSICS_MAGNET_PULL).toFixed(2)}px`)
        magnetTarget.style.setProperty('--app-magnet-y', `${clampMagnet(offsetY * SHARED_PHYSICS_MAGNET_PULL).toFixed(2)}px`)
      }

      if (tiltTarget?.isConnected) {
        const rect = tiltTarget.getBoundingClientRect()
        if (rect.width && rect.height) {
          const ratioX = (pointer.x - rect.left) / rect.width - 0.5
          const ratioY = (pointer.y - rect.top) / rect.height - 0.5
          tiltTarget.style.setProperty('--app-tilt-x', `${(-ratioY * SHARED_PHYSICS_TILT_MAX_DEG).toFixed(2)}deg`)
          tiltTarget.style.setProperty('--app-tilt-y', `${(ratioX * SHARED_PHYSICS_TILT_MAX_DEG).toFixed(2)}deg`)
        }
      }
    }

    const scheduleRender = () => {
      if (!frameRequest) {
        frameRequest = requestAnimationFrame(render)
      }
    }

    const onPointerMove = (event: PointerEvent) => {
      if (isDisabled() || event.pointerType !== 'mouse' || !finePointer.matches) {
        return
      }

      pointer.x = event.clientX
      pointer.y = event.clientY

      const hoveredMagnet = closestMatch(event.target, MAGNET_SELECTOR)
      const nextMagnet = hoveredMagnet ?? (magnetTarget?.isConnected && isNearMagnet(magnetTarget) ? magnetTarget : null)
      if (nextMagnet !== magnetTarget) {
        clearMagnet()
        magnetTarget = nextMagnet
      }

      const nextTilt = closestMatch(event.target, TILT_SELECTOR)
      if (nextTilt !== tiltTarget) {
        clearTilt()
        tiltTarget = nextTilt
      }

      scheduleRender()
    }

    const spawnRipple = (event: PointerEvent, target: HTMLElement) => {
      const frame = document.createElement('div')
      frame.className = 'app-physics__frame'
      const rect = fitFrame(frame, target)

      const size = Math.hypot(rect.width, rect.height) * 2
      const ripple = document.createElement('span')
      ripple.className = 'app-physics__ripple'
      ripple.style.width = `${size}px`
      ripple.style.height = `${size}px`
      ripple.style.left = `${event.clientX - rect.left - size / 2}px`
      ripple.style.top = `${event.clientY - rect.top - size / 2}px`

      frame.append(ripple)
      layer.append(frame)

      const animation = ripple.animate(
        [
          { transform: 'scale(0)', opacity: 1 },
          { transform: 'scale(1)', opacity: 0 },
        ],
        { duration: SHARED_PHYSICS_RIPPLE_DURATION_MS, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
      )
      const removeFrame = () => frame.remove()
      animation.onfinish = removeFrame
      animation.oncancel = removeFrame
    }

    const onPointerDown = (event: PointerEvent) => {
      if (isDisabled() || event.button !== 0) {
        return
      }

      const target = closestMatch(event.target, RIPPLE_SELECTOR)
      if (target && !target.matches(':disabled, [aria-disabled="true"]')) {
        spawnRipple(event, target)
      }
    }

    document.addEventListener('pointermove', onPointerMove, { passive: true })
    document.addEventListener('pointerdown', onPointerDown, { passive: true })
    document.documentElement.addEventListener('pointerleave', releaseAll)
    window.addEventListener('scroll', releaseAll, { passive: true, capture: true })
    window.addEventListener('blur', releaseAll)
  },
})
